/* ===========================================================
   Custom Shipping + Free Gift Progress Bar
   - Free shipping threshold
   - Auto-add / auto-remove free gift by SKU
   - Listens to theme cart PubSub + falls back to /cart.js
   =========================================================== */
(function () {
  'use strict';

  // The shipping-gift-bar snippet is rendered twice on /cart (once via the
  // global cart-drawer in theme.liquid, once via main-cart-items.liquid), so
  // the <script src> tag is included twice and browsers execute both. Without
  // this guard the IIFE runs with two independent state objects, two fetch
  // wrappers and two pubsub handlers — and the cross-instance race in addGift
  // produces a duplicate free-gift line.
  if (window.__sgbInitialized) return;
  window.__sgbInitialized = true;

  // Config comes from theme settings via window.__SGB_CONFIG__ (populated by
  // snippets/shipping-gift-bar.liquid). Fallbacks only apply if the snippet
  // wasn't rendered for some reason.
  var FROM_LIQUID = window.__SGB_CONFIG__ || {};
  var CONFIG = {
    enable: FROM_LIQUID.enable !== false,
    giftEnable: FROM_LIQUID.giftEnable !== false,
    freeShippingThreshold: Number(FROM_LIQUID.freeShippingThreshold) || 8900,
    giftThreshold: Number(FROM_LIQUID.giftThreshold) || 12000,
    giftProperty: FROM_LIQUID.giftProperty || '_free_gift',
    giftDiscountCode: FROM_LIQUID.giftDiscountCode || '',
    fsCheckEnable:     !!FROM_LIQUID.fsCheckEnable,
    fsQualifyingTag:   (FROM_LIQUID.fsQualifyingTag || '').toLowerCase(),
    fsExcludedHandle:  FROM_LIQUID.fsExcludedHandle || '',
    fsNoticeText:      FROM_LIQUID.fsNoticeText || '',
    shipText: FROM_LIQUID.shipText || '[amount] left to unlock free shipping!',
    giftText: FROM_LIQUID.giftText || '[amount] left to unlock a free gift!',
    allText:  FROM_LIQUID.allText  || "You've unlocked free shipping + a free gift!",
    currency: FROM_LIQUID.currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
  };

  // Gift variant id comes exclusively from Theme settings → Shipping Bar →
  // Free gift product (resolved to a variant id by Liquid in the snippet).
  // No other source can supply it — guarantees the selected product is the
  // only one our auto-add flow will ever touch.
  var state = {
    giftVariantId: (FROM_LIQUID.giftVariantId && Number(FROM_LIQUID.giftVariantId)) || null,
    giftProductId: (FROM_LIQUID.giftProductId && Number(FROM_LIQUID.giftProductId)) || null,
    giftLookupTried: false,
    syncing: false,
    lastSyncKey: null,
    firstRenderDone: false,
    hasNonQualifying: false,
    tagCache: {},         // handle -> [tags]
    excludedIds: null,    // null = not fetched; {} = fetched (map of product_id -> true)
    addingGiftPromise: null, // in-flight /cart/add.js for the gift; concurrent callers reuse this single promise instead of firing duplicates
    lastCart: null,       // cached cart state from the most recent update cycle
    internalCartCallCount: 0 // >0 while our own /cart/{add,change}.js is in flight; the fetch interceptor reads this to skip scheduling a redundant update() for our internal traffic
  };

  /* ---------- utilities ---------- */

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function escapeText(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Replace [amount] in a user-configurable template string with a styled span.
  // Template text is escaped; the injected span is trusted HTML we control.
  function substituteAmount(template, cents) {
    var escaped = escapeText(template);
    var span = '<span class="sgb__amount" data-sgb-amount>' + escapeText(formatMoney(cents)) + '</span>';
    return escaped.replace(/\[amount\]/g, span);
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  // A line is "our" auto-gift only when it carries the _free_gift property we
  // stamped at add time. A customer-added copy of the same SKU does not match —
  // we never auto-remove items the customer added themselves.
  function isAutoGift(item) {
    if (!item || !item.properties) return false;
    // Any truthy value of the gift property — older sessions may have
    // stamped 'yes', '1', or boolean true depending on add path. Strict
    // 'true'/true matching let drift lines (same product, _free_gift with
    // a different value) escape detection, so dedupe never claimed them.
    return !!item.properties[CONFIG.giftProperty];
  }

  // The free-gift architecture on this store is single-source: theme JS
  // (System B) is the only thing that adds the gift line, stamping it with
  // _free_gift. The "Free_Gift" entry in theme settings is a discount CODE
  // (applied session-wide via /discount/Free_Gift) that zeroes the line at
  // checkout — it does not server-side-add a gift line. So a "gift line"
  // is exactly "a line carrying our _free_gift property"; nothing else.
  function isGiftLine(item) {
    return isAutoGift(item);
  }

  // findGiftLines and findThemeGiftLines are equivalent under the
  // single-source architecture, but kept as separate names so callers
  // declare *intent*: "is there any gift?" vs "what can I /cart/change.js
  // qty=0 safely?". If the merchant ever activates a BXGY automatic
  // discount in admin, only `findGiftLines` will need to widen.
  function findGiftLines(cart) {
    var lines = [];
    if (!cart || !cart.items) return lines;
    for (var i = 0; i < cart.items.length; i++) {
      if (isGiftLine(cart.items[i])) lines.push(cart.items[i]);
    }
    return lines;
  }

  function findThemeGiftLines(cart) {
    var lines = [];
    if (!cart || !cart.items) return lines;
    for (var i = 0; i < cart.items.length; i++) {
      if (isAutoGift(cart.items[i])) lines.push(cart.items[i]);
    }
    return lines;
  }

  function findGiftLine(cart) {
    return findGiftLines(cart)[0] || null;
  }

  function getEligibleSubtotal(cart) {
    if (!cart || !cart.items) return 0;
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      // Exclude BOTH theme-added and BXGY-added gift lines from eligibility
      // math — otherwise the BXGY's $0 line counts as eligible (it doesn't
      // contribute to the threshold) which only matters at the boundary,
      // but more importantly, "qualifies" is recomputed after each sync and
      // would flip-flop if the gift is alternately included/excluded.
      if (isGiftLine(it)) continue;
      total += it.final_line_price;
    }
    return total;
  }

  // Key includes (a) eligible subtotal, (b) number of gift lines and (c) total
  // gift qty. Including line count + qty means a drift state (duplicate lines
  // or qty>1) produces a key distinct from the desired "1 line, qty 1" state,
  // so the lastSyncKey gate cannot lock in a broken cart.
  function syncKeyOf(cart) {
    var eligible = getEligibleSubtotal(cart);
    var giftLines = findGiftLines(cart);
    var totalQty = 0;
    for (var i = 0; i < giftLines.length; i++) totalQty += giftLines[i].quantity;
    return eligible + '|' + giftLines.length + '|' + totalQty;
  }

  /* ---------- gift variant resolution ---------- */
  // Primary source: Liquid resolves the variant id from the product picker in
  // Theme Settings → Shipping Bar → Free gift product, and writes it to
  // window.__SGB_CONFIG__.giftVariantId. If that didn't happen (product
  // unpublished, no variants, or theme settings not saved since picker was
  // added), we try a one-shot /products/<handle>.js fetch using the handle
  // Liquid also wrote out.
  function resolveGiftVariantId() {
    if (state.giftVariantId) return Promise.resolve(state.giftVariantId);

    var handle = FROM_LIQUID.giftProductHandle;
    if (!handle) {
      if (window.console && console.warn) {
        console.warn('[shipping-gift-bar] No gift product selected. ' +
          'Open Theme editor → Theme settings → Shipping Bar → Free gift product, pick a product, and save.');
      }
      return Promise.resolve(null);
    }

    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (prod) {
        var variant =
          (prod.variants || []).filter(function (v) { return v.available; })[0] ||
          (prod.variants || [])[0];
        if (variant && variant.id) {
          state.giftVariantId = variant.id;
          return variant.id;
        }
        if (window.console && console.warn) {
          console.warn('[shipping-gift-bar] Gift product "' + (FROM_LIQUID.giftProductTitle || handle) +
            '" has no available variants. Publish it to the Online Store channel and give it a purchasable variant.');
        }
        return null;
      })
      .catch(function () { return null; });
  }

  /* ---------- gift sync (add / remove to match eligibility) ---------- */

  // Returns true when the cart holds a non-auto copy of the gift product —
  // i.e. the customer added the gift product themselves (PDP, quick-add, etc.)
  // without the _free_gift property. Used to gate discount application: a
  // session-level Shopify discount code can't discriminate between lines, so
  // applying it here would also zero the customer's paid line at checkout.
  function hasManualGiftCopy(cart) {
    if (!cart || !cart.items) return false;
    var targetProductId = state.giftProductId;
    var targetVariantId = state.giftVariantId;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      // Skip gift lines from BOTH systems — the BXGY-added line is not a
      // "manual" copy even though it has no _free_gift property.
      if (isGiftLine(it)) continue;
      if (targetProductId && it.product_id === targetProductId) return true;
      if (!targetProductId && targetVariantId && it.variant_id === targetVariantId) return true;
    }
    return false;
  }

  // Attach the Shopify discount code to the cart session. redirect:'manual'
  // returns as soon as Set-Cookie headers arrive (~40ms) — no HTML download.
  // When `cart` is provided we use it for the contamination check; otherwise
  // we fetch fresh state. Skips entirely when a manual copy of the gift
  // product is in the cart — see hasManualGiftCopy.
  function applyGiftDiscount(cart) {
    // Only apply the session discount when the free-gift feature is ON in
    // theme settings. Without this guard the merchant can disable gifts
    // (sgb_gift_enable=false) but, if the discount code field is still
    // populated, the cookie keeps getting attached every time we run —
    // pinning a stale "Free_Gift" allocation against any future gift-variant
    // line a customer might add deliberately.
    if (!CONFIG.giftEnable) return Promise.resolve();
    if (!CONFIG.giftDiscountCode) return Promise.resolve();
    var ready = cart ? Promise.resolve(cart) : getCart();
    return ready.then(function (c) {
      if (hasManualGiftCopy(c)) return;
      return fetch('/discount/' + encodeURIComponent(CONFIG.giftDiscountCode), {
        credentials: 'same-origin',
        redirect: 'manual'
      }).then(function () {});
    }).catch(function () {});
  }

  function getCartSectionIds() {
    var ids = [];
    if (document.querySelector('cart-drawer-items') || document.querySelector('cart-drawer')) {
      ids.push('cart-drawer');
    }
    if (document.querySelector('cart-items')) ids.push('main-cart-items');
    ids.push('cart-icon-bubble');
    return ids;
  }

  // Apply Shopify-rendered section HTML returned by /cart/{add,change}.js
  // directly into the DOM — mirrors what the theme would do after a
  // cart-update publish, but skips the extra /cart?section_id=... round-trip.
  function applyCartSections(sectionsMap) {
    if (!sectionsMap) return;

    if (sectionsMap['cart-drawer']) {
      var wrap = document.createElement('div');
      wrap.innerHTML = sectionsMap['cart-drawer'];
      var newItems = wrap.querySelector('cart-drawer-items');
      var oldItems = document.querySelector('cart-drawer-items');
      if (newItems && oldItems) oldItems.replaceWith(newItems);
      var newFooter = wrap.querySelector('.cart-drawer__footer');
      var oldFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && oldFooter) oldFooter.replaceWith(newFooter);
      var newDrawer = wrap.querySelector('cart-drawer');
      var oldDrawer = document.querySelector('cart-drawer');
      if (newDrawer && oldDrawer) {
        oldDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }
    }

    if (sectionsMap['main-cart-items']) {
      var wrap2 = document.createElement('div');
      wrap2.innerHTML = sectionsMap['main-cart-items'];
      var newCart = wrap2.querySelector('cart-items');
      var oldCart = document.querySelector('cart-items');
      if (newCart && oldCart) oldCart.innerHTML = newCart.innerHTML;
    }

    if (sectionsMap['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        // The section response is wrapped in <div id="shopify-section-...">.
        // The theme's own cart pipeline (cart.js getSectionInnerHTML) strips
        // that wrapper before injecting; if we don't, our update produces a
        // different DOM than theme-driven updates — the cart-count-bubble
        // ends up nested an extra level deep, which makes the count look
        // misaligned/clipped on the header. Strip to match.
        var wrap3 = document.createElement('div');
        wrap3.innerHTML = sectionsMap['cart-icon-bubble'];
        var inner = wrap3.querySelector('.shopify-section');
        bubble.innerHTML = inner ? inner.innerHTML : sectionsMap['cart-icon-bubble'];
      }
    }
  }

  function addGift() {
    // Idempotency lock — concurrent callers (multiple cart-update events firing
    // around the same time, fetch interceptor + pubsub racing, etc.) share a
    // single in-flight fetch instead of each firing their own.
    if (state.addingGiftPromise) return state.addingGiftPromise;

    var p = resolveGiftVariantId().then(function (id) {
      if (!id) {
        if (window.console && console.warn) {
          console.warn('[shipping-gift-bar] addGift: no variant id available — gift cannot be added. ' +
            'Check Theme editor → Theme settings → Shipping Bar → Free gift product.');
        }
        return null;
      }
      // Verify against FRESH server state before firing /cart/add.js. The
      // upstream gates (state.syncing / state.lastSyncKey / cartData) can be
      // bypassed when syncGift is fed a stale cartData snapshot — typically
      // a parsedState from /cart/change.js generated *before* the prior gift
      // add reached the server. Without this verify, that stale snapshot
      // routes to action='add' and we end up with duplicate gift lines (or
      // qty=2 on a single line that Shopify merges). The /cart.js round-trip
      // adds ~50ms but eliminates the duplicate symptom the user reported.
      return getCart().then(function (cart) {
        if (findGiftLines(cart).length > 0) {
          // Server already has the gift. Skip the add and signal that the
          // caller should not run applyCartSections on a stale response.
          return null;
        }
        // Fire discount in parallel — cookie is usually already warmed on init.
        applyGiftDiscount();
        // Mark this as our own internal /cart/add.js so the fetch
        // interceptor below skips its update() schedule — syncGift's
        // success handler already applies sections + publishes a single
        // cartUpdate, so any extra update() from the interceptor would
        // just produce a redundant render the customer perceives as a
        // second refresh.
        state.internalCartCallCount++;
        var dec = function () { state.internalCartCallCount--; };
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: id,
              quantity: 1,
              properties: (function () { var p = {}; p[CONFIG.giftProperty] = 'true'; return p; })()
            }],
            sections: getCartSectionIds(),
            sections_url: window.location.pathname
          })
        })
          .then(function (r) {
            dec();
            return r.json().then(function (data) {
              if (!r.ok) {
                if (window.console && console.error) {
                  console.error('[shipping-gift-bar] /cart/add.js failed (' + r.status + '):', data);
                }
                return null;
              }
              return data;
            });
          })
          .catch(function (err) {
            dec();
            if (window.console && console.error) console.error('[shipping-gift-bar] /cart/add.js error:', err);
            return null;
          });
      });
    });

    state.addingGiftPromise = p;
    var release = function () { state.addingGiftPromise = null; };
    p.then(release, release);
    return p;
  }

  // Force-release the addGift lock. Called from action paths that explicitly
  // invalidate the previously-cached add response — the gift was just
  // removed/replaced, so any held promise no longer reflects cart state.
  function clearAddGiftLock() { state.addingGiftPromise = null; }

  // Debug helper — paste `window.__sgbAddGiftNow()` in the browser console
  // to force an add and see exactly what happens.
  window.__sgbAddGiftNow = function () {
    return addGift().then(function (resp) {
      console.log('[shipping-gift-bar] addGift response:', resp);
      if (resp && resp.sections) applyCartSections(resp.sections);
      return resp;
    });
  };

  // Force-cleanup helper — paste `window.__sgbForceDedupe()` in DevTools to
  // wipe ALL theme-added _free_gift lines and re-add a single fresh one.
  // Recovery path for stale sessions where the cart locked into a duplicate
  // state and the normal sync didn't heal it (e.g. a variant max-qty rule
  // was added in admin AFTER the duplicate was created, blocking the inner
  // /cart/add.js until the dupes are removed).
  window.__sgbForceDedupe = function () {
    state.lastSyncKey = null;
    state.addingGiftPromise = null;
    state.syncing = false;
    return getCart().then(function (cart) {
      var theme = findThemeGiftLines(cart);
      if (!theme.length) return cart;
      return removeLines(theme).then(function () {
        clearAddGiftLock();
        return addGift().then(function (resp) {
          if (resp && resp.sections) applyCartSections(resp.sections);
          notifyCartUpdate();
          return resp;
        });
      });
    });
  };

  // One-call diagnostic — paste `window.__sgbDebug()` into DevTools console.
  // Prints everything needed to pinpoint why a newly-selected gift product
  // isn't being added.
  window.__sgbDebug = function () {
    var lines = [
      '— SGB debug —',
      'FROM_LIQUID (from theme settings → render-time Liquid):',
      '  bar enabled:         ' + CONFIG.enable,
      '  gift enabled:        ' + CONFIG.giftEnable,
      '  gift threshold (¢):  ' + CONFIG.giftThreshold,
      '  resolved variant id: ' + state.giftVariantId,
      '  product handle:      ' + (FROM_LIQUID.giftProductHandle || '(none)'),
      '  product title:       ' + (FROM_LIQUID.giftProductTitle  || '(none)'),
      '  discount code:       ' + (CONFIG.giftDiscountCode       || '(empty)')
    ];
    console.log(lines.join('\n'));
    return getCart().then(function (cart) {
      var eligible = getEligibleSubtotal(cart);
      var gift = findGiftLine(cart);
      console.log('Cart state:');
      console.log('  total items:         ' + (cart.items ? cart.items.length : 0));
      console.log('  eligible subtotal:   ' + eligible + ' ¢');
      console.log('  threshold met:       ' + (eligible >= CONFIG.giftThreshold));
      console.log('  auto-gift present:   ' + (gift ? ('yes (line ' + gift.key + ', variant ' + gift.variant_id + ')') : 'no'));
      if (gift && state.giftVariantId && gift.variant_id !== state.giftVariantId) {
        console.warn('  ⚠ gift in cart uses an OLD variant id. Cross-variant replace will run on next sync.');
      }
      if (!state.giftVariantId) {
        console.error('  ✗ No resolved variant id. Theme setting → Shipping Bar → Free gift product is not resolving.');
      }
      return cart;
    });
  };

  function changeLine(lineKey, quantity) {
    // Internal /cart/change.js — bumped counter is read by the fetch
    // interceptor so it skips scheduling another update(); syncGift's
    // own success handler already applies sections.
    state.internalCartCallCount++;
    var dec = function () { state.internalCartCallCount--; };
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity,
        sections: getCartSectionIds(),
        sections_url: window.location.pathname
      })
    }).then(function (r) { dec(); return r.json(); }).catch(function () { dec(); return null; });
  }

  // Sequentially set qty=0 for every line. Returns the last response (the one
  // whose `sections` reflect the final cart state) so applyCartSections can
  // patch the DOM in one go.
  function removeLines(lines) {
    var lastResp = null;
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return changeLine(line.key, 0).then(function (resp) {
          lastResp = resp;
          return resp;
        });
      });
    }, Promise.resolve(null)).then(function () { return lastResp; });
  }

  function syncGift(cart) {
    // Never run two sync operations concurrently.
    if (state.syncing) return Promise.resolve(cart);

    var key = syncKeyOf(cart);
    var eligible = getEligibleSubtotal(cart);
    // giftLines vs themeGiftLines are equivalent under the current
    // single-source architecture (theme JS is the only thing that adds the
    // gift line; the "Free_Gift" entry in settings is a discount CODE
    // applied via session cookie, not a BXGY automatic). They're kept as
    // separate names so the action paths declare intent — "is there any
    // gift to detect?" vs "what's safe to /cart/change.js qty=0?".
    var giftLines = findGiftLines(cart);
    var giftLine = giftLines[0] || null;
    var themeGiftLines = findThemeGiftLines(cart);
    var themeGiftLine = themeGiftLines[0] || null;
    var qualifies = eligible >= CONFIG.giftThreshold;

    // Drift state we MUST always heal, even when the syncKey happens to
    // match the previous run (e.g. a dedupe attempt that ended in the
    // same drift state because the inner /cart/add.js failed with a
    // variant max-qty error). Without this bypass, the cart locks into
    // the bad state until something else bumps the eligible subtotal —
    // which is exactly the user-reported symptom of two gift lines
    // coexisting indefinitely after a stale session.
    var driftState = themeGiftLines.length > 1 ||
      (themeGiftLine && themeGiftLine.quantity !== 1);

    // No real price change since last sync — nothing to do. Guards against
    // spurious add/remove during DOM refreshes, page loads, or any code
    // path that calls update() without the cart subtotal actually moving.
    if (state.lastSyncKey === key && !driftState) return Promise.resolve(cart);

    // If our gift line no longer matches the currently-configured variant
    // (merchant picked a different product in settings), swap it.
    var wrongVariant = themeGiftLine && state.giftVariantId &&
      themeGiftLine.variant_id !== state.giftVariantId;

    // Stale-gift detection — when the merchant flips "Enable free gift" off
    // OR clears the gift product in theme settings, any gift line already
    // sitting in a customer's cart is stale.
    var giftFeatureOff = themeGiftLine && (!CONFIG.giftEnable || !state.giftVariantId);

    // Duplicate cleanup — concurrent /cart/add.js calls (now prevented by
    // the addGift idempotency lock, but possible from older sessions or
    // multi-tab races) can leave the cart with 2+ theme-added gift lines.
    // The qty-35 + qty-7 _free_gift duplicate from a stale storefront
    // session is exactly this case.
    var hasDupes = themeGiftLines.length > 1;

    var action = null;
    if (giftFeatureOff) action = 'remove';
    else if (!CONFIG.giftEnable) action = null;
    else if (qualifies && !giftLine && !hasManualGiftCopy(cart) && state.giftVariantId) action = 'add';
    else if (!qualifies && themeGiftLines.length > 0) action = 'remove';
    else if (qualifies && hasDupes) action = 'dedupe';
    else if (qualifies && wrongVariant) action = 'replace';
    else if (qualifies && themeGiftLine && themeGiftLine.quantity !== 1) action = 'fix-qty';

    if (!action) {
      state.lastSyncKey = key;
      return Promise.resolve(cart);
    }

    state.syncing = true;
    // Block checkout for the duration of any cart-mutating sync — without this
    // a click that lands between threshold-crossing and gift-add reaching the
    // server lets the customer check out without the gift line.
    setCheckoutLoading(true);
    var op;
    if (action === 'add') op = addGift();
    else if (action === 'remove') {
      // Any held add-lock now points at a stale response (gift just left the
      // cart). Clear it so a future qualifying state can fire a fresh add.
      clearAddGiftLock();
      op = removeLines(themeGiftLines);
    }
    else if (action === 'replace') {
      // Remove every (stale) theme gift line, then add the current one.
      // Clear the lock between so the inner addGift fires fresh.
      op = removeLines(themeGiftLines).then(function () { clearAddGiftLock(); return addGift(); });
    }
    else if (action === 'dedupe') {
      // Remove ALL duplicate theme gift lines, then add a single fresh one.
      op = removeLines(themeGiftLines).then(function () { clearAddGiftLock(); return addGift(); });
    }
    else op = changeLine(themeGiftLine.key, 1);

    return op
      .then(function (response) {
        // Apply sections from the response straight to the DOM — no extra
        // fetch round-trip. This is what makes add/remove feel instant.
        if (response && response.sections) {
          applyCartSections(response.sections);
        }

        // Ask for fresh cart state (cheap — just JSON). Use it to render the
        // shipping bar AND to validate that gift state matches eligibility.
        // state.syncing stays TRUE until lastSyncKey is updated, so a
        // concurrent update() can't slip in with stale cartData and re-fire
        // an add against a cart that already has the gift.
        return getCart().then(function (updated) {
          if (updated && updated.items) state.lastCart = updated;
          state.lastSyncKey = syncKeyOf(updated);
          state.syncing = false;
          render(updated);

          var eligibleNow = getEligibleSubtotal(updated);
          var giftLinesNow = findGiftLines(updated);
          var qualifiesNow = eligibleNow >= CONFIG.giftThreshold;
          var drift =
            (qualifiesNow && giftLinesNow.length === 0) ||
            (!qualifiesNow && giftLinesNow.length > 0) ||
            (qualifiesNow && giftLinesNow.length > 1) ||
            (qualifiesNow && giftLinesNow.length === 1 && giftLinesNow[0].quantity !== 1);
          if (drift) {
            state.lastSyncKey = null;
            // Loader stays on through the follow-up sync — update() will
            // toggle it back off when the cart is actually settled.
            update();
            return updated;
          }
          // No drift — release the checkout lock and tell the theme the cart
          // changed so any of its own renderers re-fetch against the
          // post-gift-add state (fixes the case where a stale cart-update
          // section response had already overwritten our injected gift line).
          setCheckoutLoading(false);
          if (action === 'add' || action === 'replace' || action === 'dedupe') {
            notifyCartUpdate();
          }
          return updated;
        });
      })
      .catch(function () {
        state.syncing = false;
        setCheckoutLoading(false);
        return cart;
      });
  }

  // The theme declares `subscribe` / `publish` as plain top-level functions in
  // assets/pubsub.js and `PUB_SUB_EVENTS` as a top-level `const` in
  // assets/constants.js. `const` at script-top-level is NOT exposed on window,
  // so we must look these up via bare-name script-scope resolution (which
  // works inside this IIFE).
  function getPubSubEvents() {
    try { return (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS : null; }
    catch (e) { return null; }
  }
  function getSubscribe() {
    try { return (typeof subscribe === 'function') ? subscribe : null; }
    catch (e) { return null; }
  }
  function getPublish() {
    try { return (typeof publish === 'function') ? publish : null; }
    catch (e) { return null; }
  }

  // Let the theme handle DOM re-rendering (surgical, no flicker).
  function notifyCartUpdate() {
    var pub = getPublish();
    var events = getPubSubEvents();
    if (pub && events && events.cartUpdate) {
      pub(events.cartUpdate, { source: 'sgb-gift' });
    }
  }

  // Selector covers every checkout entry point in the theme (drawer + cart page).
  function getCheckoutButtons() {
    return document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
    );
  }

  // Visually + functionally gate checkout while the gift is being reconciled.
  // Adds the .sgb-syncing class for the CSS spinner and toggles the disabled
  // attribute so keyboard activation is blocked too.
  function setCheckoutLoading(loading) {
    var btns = getCheckoutButtons();
    Array.prototype.forEach.call(btns, function (btn) {
      if (loading) {
        btn.classList.add('sgb-syncing');
        if (btn.tagName === 'BUTTON' || btn.tagName === 'INPUT') {
          if (!btn.dataset.sgbWasDisabled) {
            btn.dataset.sgbWasDisabled = btn.disabled ? '1' : '0';
          }
          btn.disabled = true;
        } else {
          btn.setAttribute('aria-disabled', 'true');
        }
      } else {
        btn.classList.remove('sgb-syncing');
        if (btn.tagName === 'BUTTON' || btn.tagName === 'INPUT') {
          if (btn.dataset.sgbWasDisabled === '0') btn.disabled = false;
          delete btn.dataset.sgbWasDisabled;
        } else {
          btn.removeAttribute('aria-disabled');
        }
      }
    });
  }

  /* ---------- DOM re-render is owned by the theme's own cart pipeline ---------- */

  /* ---------- render ---------- */

  function render(cart) {
    var bars = document.querySelectorAll('[data-shipping-gift-bar]');
    if (!bars.length) return;

    // Hide when cart is empty — backstop for the case where the bar's surgical
    // section update leaves a stale bar element in the DOM after the last item
    // (or auto-gift) has been removed. Initial page load is gated by Liquid.
    var isEmpty = !cart || !cart.item_count;
    Array.prototype.forEach.call(bars, function (bar) {
      bar.style.display = isEmpty ? 'none' : '';
    });
    if (isEmpty) return;

    var eligible = getEligibleSubtotal(cart);
    var shipT = CONFIG.freeShippingThreshold;
    var giftT = CONFIG.giftThreshold;
    var remainingShip = Math.max(shipT - eligible, 0);
    var remainingGift = Math.max(giftT - eligible, 0);

    // When the gift is disabled, the bar scale ends at the shipping threshold.
    var giftPos, shipPos, fillPct;
    if (!CONFIG.giftEnable) {
      giftPos = 100;
      shipPos = 100;
      fillPct = Math.min((eligible / shipT) * 100, 100);
      if (eligible >= shipT) fillPct = 100;
    } else {
      giftPos = 90;
      shipPos = (shipT / giftT) * giftPos;
      fillPct = Math.min((eligible / giftT) * giftPos, 100);
      if (eligible >= giftT) fillPct = 100;
    }

    // Build the message from configurable templates with [amount] substitution.
    // If the cart has any non-qualifying item (tag / excluded collection check),
    // override with the settings-driven notice text — the "$X left" message
    // would be misleading since no amount added will make the cart qualify.
    var messageHtml;
    if (CONFIG.fsCheckEnable && state.hasNonQualifying && cart && cart.item_count > 0) {
      messageHtml = escapeText(CONFIG.fsNoticeText);
    } else if (CONFIG.giftEnable && eligible >= giftT) {
      messageHtml = escapeText(CONFIG.allText);
    } else if (CONFIG.giftEnable && eligible >= shipT) {
      messageHtml = substituteAmount(CONFIG.giftText, remainingGift);
    } else if (!CONFIG.giftEnable && eligible >= shipT) {
      messageHtml = escapeText(CONFIG.allText);
    } else {
      var show = remainingShip > 0 ? remainingShip : shipT;
      messageHtml = substituteAmount(CONFIG.shipText, show);
    }

    Array.prototype.forEach.call(bars, function (bar) {
      var fill = bar.querySelector('[data-sgb-fill]');
      var ball = bar.querySelector('[data-sgb-ball]');
      var amountEl = bar.querySelector('[data-sgb-amount]');
      var textEl = bar.querySelector('[data-sgb-text]');
      var shipM = bar.querySelector('[data-sgb-milestone="ship"]');
      var giftM = bar.querySelector('[data-sgb-milestone="gift"]');
      var shipPrice = bar.querySelector('[data-sgb-price-ship]');
      var giftPrice = bar.querySelector('[data-sgb-price-gift]');
      var shipTick = bar.querySelector('[data-sgb-tick-ship]');
      var giftTick = bar.querySelector('[data-sgb-tick-gift]');

      // Each bar element (drawer or page) jumps to its current position
      // without animation the very first time we render into it. After that,
      // width/left changes transition smoothly forward from the current value.
      var fresh = !bar.dataset.sgbReady;
      if (fresh) bar.classList.add('sgb--no-transition');

      if (fill) fill.style.width = fillPct + '%';
      if (ball) ball.style.left = fillPct + '%';

      if (fresh) {
        void bar.offsetWidth;
        bar.classList.remove('sgb--no-transition');
        bar.dataset.sgbReady = '1';
      }

      if (shipM) shipM.style.setProperty('--sgb-pos', shipPos + '%');
      if (giftM) giftM.style.setProperty('--sgb-pos', giftPos + '%');
      if (shipTick) shipTick.style.setProperty('--sgb-pos', shipPos + '%');
      if (giftTick) giftTick.style.setProperty('--sgb-pos', giftPos + '%');

      if (shipPrice) shipPrice.textContent = formatMoney(shipT);
      if (giftPrice) giftPrice.textContent = formatMoney(giftT);

      // Replace full message HTML (contains the red amount span inline).
      var msg = bar.querySelector('[data-sgb-message]');
      if (msg) msg.innerHTML = messageHtml;

      if (shipM) shipM.classList.toggle('is-reached', eligible >= shipT);
      if (giftM) giftM.classList.toggle('is-reached', eligible >= giftT);
      bar.classList.toggle('is-shipping-reached', eligible >= shipT);
      bar.classList.toggle('is-gift-reached', eligible >= giftT);
      bar.classList.toggle('is-all-reached', eligible >= giftT);
    });

    markGiftLines(cart);
    overrideTotals(cart);
  }

  // Safety: if the gift variant ever ends up in cart at a non-zero price
  // (e.g. misconfigured variant price), subtract it from the displayed total.
  // With the variant priced at $0 in admin, this is a no-op.
  function overrideTotals(cart) {
    if (!cart) return;
    var gift = findGiftLine(cart);
    var giftPrice = gift ? gift.final_line_price : 0;
    if (!giftPrice) return;

    var adjusted = Math.max(cart.total_price - giftPrice, 0);

    var totalEls = document.querySelectorAll(
      '.totals__total-value, .cart-footer__total-value, .totals__subtotal-value'
    );
    Array.prototype.forEach.call(totalEls, function (el) {
      var currency = /\b([A-Z]{3})\b/.exec(el.textContent);
      el.textContent = formatMoney(adjusted) + (currency ? ' ' + currency[1] : '');
    });
  }

  function markGiftLines(cart) {
    if (!cart || !cart.items) return;
    // Clear stale markers first.
    var stale = document.querySelectorAll('.cart-item.sgb-gift-line');
    for (var s = 0; s < stale.length; s++) stale[s].classList.remove('sgb-gift-line');

    // STRICT: only a true gift line — either ours (carries _free_gift) or
    // Shopify's BXGY-added line (gift variant + zero line price). A
    // customer-purchased copy of the same product is never zero-priced and
    // is never styled as free.
    var idx = -1;
    for (var i = 0; i < cart.items.length; i++) {
      if (isGiftLine(cart.items[i])) { idx = i; break; }
    }
    if (idx < 0) return;
    var lineNum = idx + 1;

    var rows = [];
    var drawerRow = document.getElementById('CartDrawer-Item-' + lineNum);
    if (drawerRow) rows.push(drawerRow);
    var cartRow = document.getElementById('CartItem-' + lineNum);
    if (cartRow) rows.push(cartRow);

    rows.forEach(function (row) {
      row.classList.add('sgb-gift-line');
    });
  }

  /* ---------- free-shipping eligibility (tag + excluded-collection) ---------- */
  function fetchProductTags(handle) {
    if (!handle) return Promise.resolve([]);
    if (state.tagCache[handle]) return Promise.resolve(state.tagCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (prod) {
        var tags = (prod && prod.tags) ? prod.tags.map(function (t) { return String(t).toLowerCase(); }) : [];
        state.tagCache[handle] = tags;
        return tags;
      })
      .catch(function () { return []; });
  }

  function fetchExcludedIds() {
    var handle = CONFIG.fsExcludedHandle;
    if (!handle) { state.excludedIds = state.excludedIds || {}; return Promise.resolve(state.excludedIds); }
    if (state.excludedIds) return Promise.resolve(state.excludedIds);
    return fetch('/collections/' + handle + '/products.json?limit=250', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var set = {};
        (data && data.products || []).forEach(function (p) { set[p.id] = true; });
        state.excludedIds = set;
        return set;
      })
      .catch(function () { state.excludedIds = {}; return state.excludedIds; });
  }

  function evaluateEligibility(cart) {
    if (!CONFIG.fsCheckEnable) { state.hasNonQualifying = false; return Promise.resolve(false); }
    if (!cart || !cart.items || !cart.items.length) { state.hasNonQualifying = false; return Promise.resolve(false); }
    return fetchExcludedIds().then(function (excluded) {
      var checks = cart.items.map(function (item) {
        if (isGiftLine(item)) return Promise.resolve(true); // gift line (theme or BXGY) doesn't block eligibility
        return fetchProductTags(item.handle).then(function (tags) {
          var tagOk = tags.indexOf(CONFIG.fsQualifyingTag) !== -1;
          var inExcluded = !!excluded[item.product_id];
          return tagOk && !inExcluded;
        });
      });
      return Promise.all(checks).then(function (rows) {
        var hasNonQ = rows.indexOf(false) !== -1;
        state.hasNonQualifying = hasNonQ;
        return hasNonQ;
      });
    });
  }

  /* ---------- main update cycle ---------- */

  function update(maybeCart) {
    var p = (maybeCart && maybeCart.items) ? Promise.resolve(maybeCart) : getCart();
    return p.then(function (cart) {
      // Cache cart state so the next /cart/change.js can be predicted against
      // a recent snapshot — without this the pre-fire path can't decide
      // whether the change will cross the gift threshold.
      if (cart && cart.items) state.lastCart = cart;
      return syncGift(cart).then(function (finalCart) {
        var settled = finalCart || cart;
        // Resolve eligibility before rendering so the message reflects it.
        return evaluateEligibility(settled).then(function () {
          render(settled);
          return settled;
        });
      });
    }).catch(function () {});
  }

  /* ---------- event wiring ---------- */

  function wire() {
    // Retry until pubsub.js + constants.js are parsed and the bindings exist.
    (function subscribeWhenReady(attempts) {
      var sub = getSubscribe();
      var events = getPubSubEvents();
      if (sub && events && events.cartUpdate) {
        sub(events.cartUpdate, function (event) {
          // Skip events we just published ourselves via notifyCartUpdate.
          // syncGift has already settled, applyCartSections has run and
          // lastSyncKey is up-to-date, so re-running update() here would
          // do a no-op syncGift plus a redundant render — exactly the
          // "extra refresh" the customer reported after a gift was added.
          if (event && event.source === 'sgb-gift') return;
          // Theme fires cartUpdate with { source, cartData } — use that cart
          // state directly when present to skip an extra /cart.js round-trip.
          var data = event && event.cartData;
          update(data && data.items ? data : undefined);
        });
        return;
      }
      if (attempts >= 40) return; // ~4s timeout — give up silently.
      setTimeout(function () { subscribeWhenReady(attempts + 1); }, 100);
    })(0);
    // Legacy / generic cart refresh hooks
    document.addEventListener('cart:updated', function () { update(); });
    document.addEventListener('cart:refresh', function () { update(); });
    document.addEventListener('cart:added', function () { update(); });

    // Catch any /cart/* mutation and fire update() so syncGift re-evaluates.
    // (Pre-fire was removed — running /cart/add.js in parallel with
    // /cart/change.js could race against Shopify's cart serialization and
    // produce duplicate gift lines / qty=2 transitions visible to the user.
    // The gift add now strictly follows the cart-update event, with an
    // internal verify step in addGift catching stale-cartData callers.)
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';

      var res = _fetch.apply(this, arguments);
      // Skip our OWN internal /cart/{add,change}.js traffic — addGift /
      // changeLine bump state.internalCartCallCount around their fetches
      // and syncGift's success handler already applies sections + fires
      // a single notifyCartUpdate. Without this guard the interceptor
      // schedules a second update() per internal call, which the
      // customer perceives as the cart drawer refreshing again right
      // after the gift appears.
      if (state.internalCartCallCount > 0) return res;
      if (/\/cart\/(add|change|update|clear)(\.js|\.json)?/i.test(url) && method.toUpperCase() !== 'GET') {
        res.then(function () { update(); }).catch(function () {});
      }
      return res;
    };

    // Theme replaces cart items / footer async after cart-update. Re-apply
    // our gift line markings + totals override once that DOM settles.
    var debounceTimer = null;
    var bodyObs = new MutationObserver(function (mutations) {
      var relevant = false;
      for (var i = 0; i < mutations.length && !relevant; i++) {
        var m = mutations[i];
        if (m.type !== 'childList') continue;
        var t = m.target;
        if (!t) continue;
        var tag = t.tagName;
        // Only react to structural swaps of the cart item containers.
        if (tag === 'CART-DRAWER-ITEMS' || tag === 'CART-ITEMS') { relevant = true; break; }
        var cls = t.classList;
        if (cls && (cls.contains('js-contents') || cls.contains('cart-drawer__footer'))) {
          relevant = true;
        }
      }
      if (!relevant) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        getCart().then(function (cart) {
          markGiftLines(cart);
          overrideTotals(cart);
          render(cart);
        }).catch(function () {});
      }, 0);
    });
    bodyObs.observe(document.body, { childList: true, subtree: true });
  }

  // If the gift is in cart but its line price wasn't zeroed, re-apply the
  // discount code so checkout charges $0.
  function ensureGiftDiscountApplied() {
    if (!CONFIG.giftEnable || !CONFIG.giftDiscountCode) return;
    getCart().then(function (cart) {
      var gift = findGiftLine(cart);
      if (gift && gift.final_line_price > 0) applyGiftDiscount(cart);
    }).catch(function () {});
  }

  // Intercept Checkout clicks so the discount cookie is guaranteed fresh
  // in the session the checkout page inherits.
  // function interceptCheckout() {
  //   if (!CONFIG.giftEnable || !CONFIG.giftDiscountCode) return;
  //   var blocking = false;
  //   document.addEventListener('click', function (e) {
  //     if (blocking) return;
  //     var btn = e.target && e.target.closest && e.target.closest(
  //       'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
  //     );
  //     if (!btn) return;
  //     var form = btn.tagName === 'A' ? null : btn.closest('form');
  //     var href = btn.tagName === 'A' ? btn.href : null;
  //     e.preventDefault(); e.stopPropagation();
  //     blocking = true;
  //     getCart().then(function (cart) {
  //       var gift = findGiftLine(cart);
  //       if (!gift || gift.final_line_price === 0) return Promise.resolve();
  //       return applyGiftDiscount(cart);
  //     }).catch(function () {}).then(function () {
  //       blocking = false;
  //       if (form && typeof form.submit === 'function') form.submit();
  //       else if (href) window.location.href = href;
  //       else btn.click();
  //     });
  //   }, true);
  // }

  // Always-on (not gated by giftDiscountCode) so we can also block checkout
  // while the gift is still being reconciled. Three jobs:
  //   1. Wait for any in-flight gift add / sync to settle.
  //   2. If the cart qualifies but the gift line is missing, force-add it
  //      before the checkout request leaves the browser.
  //   3. Re-apply the discount cookie if one is configured.
  function interceptCheckout() {
    if (!CONFIG.giftEnable) return;
    var blocking = false;
    document.addEventListener('click', function (e) {
      if (blocking) return;
      var btn = e.target && e.target.closest && e.target.closest(
        'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
      );
      if (!btn) return;
      // btn.form follows the HTMLButtonElement form="…" association, so this
      // resolves correctly for the drawer/cart-page buttons that live OUTSIDE
      // their form (closest('form') would return null for those). closest is
      // the fallback for buttons inside their form (e.g. cart-notification).
      var form = btn.tagName === 'A' ? null : (btn.form || btn.closest('form'));
      var href = btn.tagName === 'A' ? btn.href : null;
      e.preventDefault(); e.stopPropagation();
      blocking = true;
      setCheckoutLoading(true);

      // Wait for any in-flight add/sync from the most recent cart mutation.
      var pending = state.addingGiftPromise || Promise.resolve();
      pending.catch(function () {}).then(function () {
        return getCart();
      }).then(function (cart) {
        var eligible = getEligibleSubtotal(cart);
        var gift = findGiftLine(cart);
        var qualifies = eligible >= CONFIG.giftThreshold;
        // Missing gift on a qualifying cart — force a sync run, then re-read.
        // This is the safety net for the user-reported case where the gift
        // never made it to the cart before the customer hit checkout.
        if (qualifies && !gift && state.giftVariantId && !hasManualGiftCopy(cart)) {
          state.lastSyncKey = null;
          return update().then(getCart);
        }
        return cart;
      }).then(function (cart) {
        var gift = cart && findGiftLine(cart);
        if (gift && gift.final_line_price > 0) return applyGiftDiscount(cart);
        return null;
      }).catch(function () {}).then(function () {
        blocking = false;
        setCheckoutLoading(false);
        if (href) {
          window.location.href = href;
        } else if (form && typeof form.requestSubmit === 'function') {
          // requestSubmit includes the submitter's name="checkout" in the
          // POST body — the field Shopify's /cart endpoint looks for to
          // redirect onward to /checkout. form.submit() drops the submitter.
          form.requestSubmit(btn);
        } else if (form && typeof form.submit === 'function') {
          form.submit();
        }
      });
    }, true);
  }

  // Re-read window.__SGB_CONFIG__ and sync our in-memory CONFIG/state to it.
  // The shipping-gift-bar snippet writes a fresh __SGB_CONFIG__ inline script
  // every time it renders. On the live storefront this only happens once (full
  // page load), so the IIFE-captured CONFIG is fine. In the theme editor,
  // however, Shopify reloads sections in-place when settings change — the new
  // markup re-runs the inline script and updates the global, but our module
  // never re-reads it without this hook. Without this, changing the gift
  // product in the editor leaves syncGift comparing against a stale variant
  // id and the "replace" branch can't fire.
  function refreshConfigFromLiquid() {
    var fresh = window.__SGB_CONFIG__;
    if (!fresh || fresh === FROM_LIQUID) return false;
    // Repoint FROM_LIQUID at the new object so resolveGiftVariantId's
    // handle/title lookup uses current values, not the snapshot from page load.
    FROM_LIQUID = fresh;
    var changed = false;

    if (fresh.enable !== undefined && (fresh.enable !== false) !== CONFIG.enable) {
      CONFIG.enable = fresh.enable !== false; changed = true;
    }
    if (fresh.giftEnable !== undefined && (fresh.giftEnable !== false) !== CONFIG.giftEnable) {
      CONFIG.giftEnable = fresh.giftEnable !== false; changed = true;
    }
    if (fresh.freeShippingThreshold && Number(fresh.freeShippingThreshold) !== CONFIG.freeShippingThreshold) {
      CONFIG.freeShippingThreshold = Number(fresh.freeShippingThreshold); changed = true;
    }
    if (fresh.giftThreshold && Number(fresh.giftThreshold) !== CONFIG.giftThreshold) {
      CONFIG.giftThreshold = Number(fresh.giftThreshold); changed = true;
    }
    if (fresh.giftDiscountCode !== undefined && fresh.giftDiscountCode !== CONFIG.giftDiscountCode) {
      CONFIG.giftDiscountCode = fresh.giftDiscountCode || ''; changed = true;
    }

    var freshVariantId = fresh.giftVariantId ? Number(fresh.giftVariantId) : null;
    var freshProductId = fresh.giftProductId ? Number(fresh.giftProductId) : null;

    if (freshVariantId !== state.giftVariantId) {
      state.giftVariantId = freshVariantId; changed = true;
    }
    if (freshProductId !== state.giftProductId) {
      state.giftProductId = freshProductId; changed = true;
    }

    // The lastSyncKey gate would otherwise block the cart re-sync that's
    // needed to swap the now-stale gift line for the new one.
    if (changed) state.lastSyncKey = null;
    return changed;
  }

  function init() {
    if (!document.querySelector('[data-shipping-gift-bar]')) return;
    wire();
    interceptCheckout();

    // Theme-editor live re-sync. shopify:section:load fires after the editor
    // re-renders any section whose settings changed; the snippet's inline
    // script has already updated window.__SGB_CONFIG__ by this point. We
    // re-read it, then drive a sync so the cart reflects the new product.
    document.addEventListener('shopify:section:load', function () {
      if (refreshConfigFromLiquid()) update();
    });

    resolveGiftVariantId().finally(function () {
      update();
      // Only apply the discount when our auto-gift (an .sgb-gift-line row
      // with properties._free_gift === 'true') is actually in the cart.
      ensureGiftDiscountApplied();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
